import axios from 'axios';
import { toaster } from '../components/ui/toaster';

// Track if we've already shown the session expired message
let sessionExpiredShown = false;

// Request deduplication cache - prevents simultaneous identical API calls
const requestCache = new Map();

// Setup axios interceptor to handle error responses globally
export const setupAxiosInterceptors = (logoutCallback) => {
    // Request interceptor for deduplication
    axios.interceptors.request.use(
        (config) => {
            // Only deduplicate GET requests (safe operations)
            if (config.method === 'get') {
                const cacheKey = `${config.method}:${config.url}`;

                // If this request is already in flight, return the cached promise
                if (requestCache.has(cacheKey)) {
                    config.adapter = () => requestCache.get(cacheKey);
                }
            }
            return config;
        },
        (error) => Promise.reject(error)
    );
    axios.interceptors.response.use(
        (response) => {
            // Clear cache for this request now that it's complete
            const cacheKey = `${response.config.method}:${response.config.url}`;
            requestCache.delete(cacheKey);
            return response;
        },
        (error) => {
            // Clear cache for failed requests
            if (error.config) {
                const cacheKey = `${error.config.method}:${error.config.url}`;
                requestCache.delete(cacheKey);
            }

            const status = error.response?.status;
            const detail = error.response?.data?.detail;

            // Handle 401 Unauthorized - session expired
            if (status === 401) {
                // Only show toast once per session
                if (!sessionExpiredShown) {
                    sessionExpiredShown = true;

                    try {
                        toaster.create({
                            title: 'Session Expired',
                            description: 'Your session has expired. Please log in again.',
                            type: 'warning',
                            duration: 3000,
                        });
                    } catch (e) {
                    }
                }

                // Call the logout callback to clear auth state
                // Pass skipApiCall=true to prevent infinite loop (token is already invalid)
                if (logoutCallback) {
                    logoutCallback(true); // true = skip API call
                }

                // Redirect to login page after a short delay to show the toast
                setTimeout(() => {
                    sessionExpiredShown = false; // Reset for next session
                    window.location.href = '/login';
                }, 1500);
            }

            // Handle 402 Payment Required - feature not entitled
            if (status === 402) {
                toaster.create({
                    title: 'Feature Not Available',
                    description: detail || 'Your account does not have access to this feature. Please contact your administrator.',
                    type: 'warning',
                    duration: 5000,
                });
            }

            // Handle 403 Forbidden - permission denied
            if (status === 403) {
                toaster.create({
                    title: 'Access Denied',
                    description: detail || 'You do not have permission to perform this action.',
                    type: 'error',
                    duration: 4000,
                });
            }

            return Promise.reject(error);
        }
    );
};
